import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import CheckCircleIcon from 'components/material-symbol/icons/CheckCircle'
import CloseIcon from 'components/material-symbol/icons/Close'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import { UPLOAD_STATUS } from './use-video-uploads'
import { useStyles } from './styles'
import { formatFileSize } from './utils'

const VideoUploadItem = ({ item, onRemove, onRetry }) => {
  const classes = useStyles()
  const { file, status, progress, error } = item
  const isUploading = status === UPLOAD_STATUS.UPLOADING

  return (
    <li className={classes.fileItem}>
      <Box className={classes.iconTile} aria-hidden="true">
        {status === UPLOAD_STATUS.UPLOADED ? (
          <CheckCircleIcon />
        ) : (
          <VideocamIcon />
        )}
      </Box>
      <Box className={classes.fileMeta}>
        <Typography className={classes.fileName} title={file.name}>
          {file.name}
        </Typography>
        {isUploading ? (
          <>
            <Typography className={classes.fileStatus}>
              Uploading… {progress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              className={classes.progress}
              aria-label={`Upload progress for ${file.name}`}
            />
          </>
        ) : null}
        {status === UPLOAD_STATUS.UPLOADED ? (
          <Typography className={classes.fileStatus}>
            Uploaded · {formatFileSize(file.size)}
          </Typography>
        ) : null}
        {status === UPLOAD_STATUS.ERROR ? (
          <Typography
            className={clsx(classes.fileStatus, classes.fileStatusError)}
            role="alert"
          >
            {error}
          </Typography>
        ) : null}
      </Box>
      <Box className={classes.fileActions}>
        {status === UPLOAD_STATUS.ERROR ? (
          <Button size="small" color="primary" onClick={() => onRetry(item.id)}>
            Retry<span className={classes.srOnly}> upload of {file.name}</span>
          </Button>
        ) : null}
        <IconButton
          size="small"
          onClick={() => onRemove(item.id)}
          aria-label={
            isUploading
              ? `Cancel upload of ${file.name}`
              : `Remove ${file.name}`
          }
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </li>
  )
}

VideoUploadItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    file: PropTypes.object.isRequired,
    status: PropTypes.string.isRequired,
    progress: PropTypes.number,
    error: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
}

const VideoUploadList = ({ items, onRemove, onRetry }) => {
  const classes = useStyles()
  if (items.length === 0) return null

  return (
    <ul className={classes.fileList} aria-label="Selected videos">
      {items.map(item => (
        <VideoUploadItem
          key={item.id}
          item={item}
          onRemove={onRemove}
          onRetry={onRetry}
        />
      ))}
    </ul>
  )
}

VideoUploadList.propTypes = {
  items: PropTypes.array.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
}

export default VideoUploadList
